import {
  DateTime,
  Duration,
  DurationObjectUnits,
  DurationUnits,
  HourNumbers,
} from 'luxon';
import { hasDefinedProperties } from './object.helpers';
import { ModelTimestamps } from '../models/base.model';
import { Coordinate } from '@shared/models/geography.model';

const units: DurationUnits[] = ['minutes', 'hours', 'days', 'years'];

export const getTimeSinceEvent = (
  time: DateTime,
  dateFormat = 'dd MMM',
): string => {
  const durations = units.reduce<DurationObjectUnits>(
    (acc, unit) => ({ ...acc, ...time.diffNow(unit).toObject() }),
    {},
  );
  const isFuture = time > DateTime.now();

  if (!hasDefinedProperties(durations)) {
    return '';
  }
  if (Math.abs(durations.hours) < 1) {
    return getTimeString(durations, 'minutes', isFuture);
  }
  if (Math.abs(durations.days) < 1) {
    return getTimeString(durations, 'hours', isFuture);
  }
  if (Math.abs(durations.days) < 7) {
    return getTimeString(durations, 'days', isFuture);
  }
  if (Math.abs(durations.years) < 1) {
    return time.toFormat(dateFormat);
  }
  return time.toFormat(`${dateFormat} yyyy`);
};

const getTimeString = (
  durations: Required<DurationObjectUnits>,
  unit: 'minutes' | 'hours' | 'days',
  isFuture: boolean,
): string => {
  const value = getFormattedNumber(durations[unit]);

  let unitString = '';
  if (unit === 'minutes') unitString = 'min';
  else if (value === 1) unitString = unit.slice(0, -1);
  else unitString = unit;

  const time = `${value < 1 ? '< 1' : value} ${unitString}`;

  return isFuture ? `in ${time}` : `${time} ago`;
};

const getFormattedNumber = (value: number): number =>
  Math.floor(Math.abs(value));

type ParsableDateTime = DateTime<boolean> | string | Date | number;

/**
 * Narrows a possibly-invalid Luxon {@link DateTime} to a validated one.
 * Needed because `isValid` is typed as `boolean` on `DateTime<boolean>` and does not act as a type predicate.
 */
const isValidDateTime = (
  dateTime: DateTime<boolean>,
): dateTime is DateTime<true> => dateTime.isValid;

/**
 * Validate a luxon datetime
 * @param dateTime
 * @returns
 */
const validateDateTime = (dateTime: DateTime<boolean>): DateTime<true> => {
  const reason = dateTime.invalidReason;
  if (isValidDateTime(dateTime)) return dateTime;
  throw new Error(`Invalid date: ${reason ?? 'unknown'}`);
};

/**
 * Parse any date-like object into a luxon datetime
 * @param dateTime
 * @returns
 */
export const toDateTime = (dateTime: ParsableDateTime): DateTime<true> => {
  if (dateTime instanceof Date) {
    return validateDateTime(DateTime.fromJSDate(dateTime));
  }
  if (typeof dateTime === 'string') {
    return validateDateTime(DateTime.fromISO(dateTime));
  }
  if (typeof dateTime === 'number') {
    return validateDateTime(DateTime.fromMillis(dateTime));
  }
  if (dateTime instanceof DateTime) {
    return validateDateTime(dateTime);
  }
  throw new Error('Invalid date type');
};

/**
 * Convert any date-like object into a date string in UTC
 * @param dateTime
 * @returns
 */
export const toDateString = (dateTime: ParsableDateTime): string =>
  toDateTime(dateTime).toUTC().toISO();

/**
 * Get timestamp in ISO format (2025-05-12T12:04:39.578Z). Use this one to make sure we use the same format everywhere.
 * @returns
 */
export const getTimestamp = (): string => toDateString(DateTime.now());

export const getTimestamps = (): ModelTimestamps => ({
  created_at: getTimestamp(),
  updated_at: getTimestamp(),
});

const publicHolidayDates: { month: number; date: number }[] = [
  { month: 1, date: 1 },
  { month: 5, date: 1 },
  { month: 12, date: 24 },
  { month: 12, date: 25 },
  { month: 12, date: 31 },
];

/**
 * Checks if current time is during business hours (8-17 on weekdays)
 */
export const isOfficeHours = (date: Date = new Date()): boolean => {
  const dayOfWeek = date.getUTCDay(); // 0 = Sunday, 1-5 = Monday-Friday
  const hours = date.getUTCHours();

  const isPublicHoliday = publicHolidayDates.some(
    (holiday) =>
      holiday.month === date.getUTCMonth() + 1 &&
      holiday.date === date.getUTCDate(),
  );

  if (isPublicHoliday) {
    return false;
  }

  // Check if it's a weekday (Monday-Friday)
  const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;

  // Check if time is between 8-17 (6-15 UTC)
  const isDuringBusinessHours = hours >= 6 && hours <= 15;

  return isWeekday && isDuringBusinessHours;
};

/**
 * Get the time difference in milliseconds between two dates if date is after compareTo the number will be positive, otherwise it will be negative
 * @param date The date to compare
 * @param compareTo The date to compare to (baseline).
 * @returns The time difference in milliseconds
 */
export const getTimeDifference = (
  date: ParsableDateTime,
  comparedTo: ParsableDateTime,
): number => toDateTime(date).diff(toDateTime(comparedTo)).toMillis();

/**
 * Check if a date is before another date
 * @param date
 * @param comparedTo
 * @returns true if date is before comparedTo, false otherwise
 */
export const isBeforeDate = (
  date: ParsableDateTime,
  comparedTo: ParsableDateTime,
): boolean => {
  // For performance reasons use native date comparison if possible
  if (date instanceof Date && comparedTo instanceof Date) {
    return date < comparedTo;
  }

  return getTimeDifference(date, comparedTo) < 0;
};

const MILLIS_PER_DAY = Duration.fromObject({ days: 1 }).toMillis();
const JULIAN_TO_UNIX_DAY_OFFSET = 2_440_587.5; // 2440587.5 is the Julian day of 1970-01-01
/**
 * Get the Julian day number for a given date
 * Julian day number is the number of days since January 1, 4713 BC (Julian calendar)
 * @param date
 * @returns The Julian day number (integer)
 */
export const getJulianDate = (date: Date): number => {
  const daysSinceEpoch = date.getTime() / MILLIS_PER_DAY;

  return daysSinceEpoch + JULIAN_TO_UNIX_DAY_OFFSET;
};

const J2000_EPOCH = 2_451_545; // Julian day number for J2000 epoch (January 1, 2000, 12:00 UTC)
const DAYS_PER_CENTURY = 36_525; // Days in 100 Julian years (365.25 days * 100 years)
/**
 * Get Julian centuries since J2000 epoch for a given Julian day number
 * @param julianDate Julian day number
 * @returns Julian centuries since J2000 (January 1, 2000, 12:00 UTC)
 */
export const getJulianCentury = (julianDate: number): number => {
  return (julianDate - J2000_EPOCH) / DAYS_PER_CENTURY;
};

/**
 * Get estimated hour number offset from UTC based on longitude.
 *  - Rounds to nearest hour.
 *  - Ignore DST and local boundaries (±1 h error can occur).
 *  - Returns 0..23 (mod 24), where 22 corresponds to UTC-2.
 */
export function timeZoneHourNumberApprox(coord: Coordinate): HourNumbers {
  // Normalize longitude to interval (-180, 180]
  const lng = ((((coord.lng + 180) % 360) + 360) % 360) - 180;

  // 15° longitude ≈ 1 hour
  const rawOffset = Math.round(lng / 15); // can be negative

  // The world uses approximately UTC−12..UTC+14
  const clamped = Math.max(-12, Math.min(14, rawOffset));

  // Map e.g. -2 -> 22, 0 -> 0, +2 -> 2
  const hourNumber = ((clamped % 24) + 24) % 24;
  return hourNumber as HourNumbers;
}
